import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { obtenerVisita, TipoItem, type Adjunto } from "../api";
import { formatearBytes, formatearFecha } from "../formato";
import VisorAdjunto from "./VisorAdjunto";

type VisitaDetalleModalProps = {
  visitaID: string | null;
  onCerrar: () => void;
  puedeEditar?: boolean;
  onEmitirReceta?: (visitaID: string, diagnostico?: string) => void;
};

const ICONOS_ITEM: Record<number, string> = {
  [TipoItem.Vacuna]: "💉",
  [TipoItem.AntiparasitarioInterno]: "🪱",
  [TipoItem.AntiparasitarioExterno]: "🧴",
  [TipoItem.Medicamento]: "💊",
  [TipoItem.Procedimiento]: "🩺",
  [TipoItem.Examen]: "🔬",
  [TipoItem.Observacion]: "📝",
};

const ETIQUETAS_TIPO_ITEM: Record<number, string> = {
  [TipoItem.Vacuna]: "Vacuna",
  [TipoItem.AntiparasitarioInterno]: "Antiparasitario interno",
  [TipoItem.AntiparasitarioExterno]: "Antiparasitario externo",
  [TipoItem.Medicamento]: "Medicamento",
  [TipoItem.Procedimiento]: "Procedimiento",
  [TipoItem.Examen]: "Examen",
  [TipoItem.Observacion]: "Observación",
};

export default function VisitaDetalleModal({
  visitaID,
  onCerrar,
  puedeEditar = false,
  onEmitirReceta,
}: VisitaDetalleModalProps) {
  const [adjuntoSeleccionado, setAdjuntoSeleccionado] = useState<Adjunto | null>(null);

  const queryVisita = useQuery({
    queryKey: ["visita", visitaID],
    queryFn: () => obtenerVisita(visitaID!),
    enabled: Boolean(visitaID),
  });

  if (!visitaID) return null;

  const v = queryVisita.data;

  return (
    <>
      <div className="modal-backdrop" onClick={onCerrar}>
        <div
          className="modal-dialog modal-visita panel"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="modal-cabecera">
            <div>
              <h2 style={{ margin: 0, fontSize: "1.25rem" }}>
                Atención clínica del {v ? formatearFecha(v.fecha) : "..."}
              </h2>
              {v && (
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.35rem" }}>
                  <span className="etiqueta neutra">{v.tipoVisitaNombre}</span>
                  {v.pesoKg != null && (
                    <span className="etiqueta neutra">⚖️ {v.pesoKg.toFixed(1)} kg</span>
                  )}
                  {v.clinicaNombre && (
                    <span className="muted" style={{ fontSize: "0.85rem" }}>
                      🏥 {v.clinicaNombre}
                    </span>
                  )}
                  {v.profesionalNombre && (
                    <span className="muted" style={{ fontSize: "0.85rem" }}>
                      👨‍⚕️ {v.profesionalNombre}
                    </span>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="boton-chico"
              onClick={onCerrar}
              title="Cerrar ventana"
            >
              ✕
            </button>
          </header>

          <div className="modal-cuerpo" style={{ maxHeight: "75vh", overflowY: "auto", padding: "1rem" }}>
            {queryVisita.isPending && (
              <div style={{ padding: "2rem", textAlign: "center" }} className="muted">
                Cargando información detallada de la atención…
              </div>
            )}

            {queryVisita.error && (
              <p className="err">{queryVisita.error.message}</p>
            )}

            {v && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                {/* Motivo y Diagnóstico */}
                {(v.motivo || v.diagnostico) && (
                  <div className="visita-evaluacion" style={{ padding: "0.75rem", borderRadius: "0.375rem" }}>
                    {v.motivo && (
                      <p className="visita-motivo" style={{ margin: "0.2rem 0" }}>
                        <strong>Motivo de consulta:</strong> {v.motivo}
                      </p>
                    )}
                    {v.diagnostico && (
                      <p className="visita-diagnostico" style={{ margin: "0.2rem 0" }}>
                        <strong>Diagnóstico / Hallazgos clínicos:</strong> {v.diagnostico}
                      </p>
                    )}
                  </div>
                )}

                {/* Items aplicados o administrados */}
                {v.items && v.items.length > 0 && (
                  <div className="visita-items">
                    <h3 style={{ fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-2)", marginBottom: "0.5rem" }}>
                      Acciones y fármacos administrados ({v.items.length}):
                    </h3>
                    <ul className="visita-items-lista">
                      {v.items.map((it) => (
                        <li key={it.visitaItemID} className="visita-item-fila">
                          <span className="visita-item-icono" title={ETIQUETAS_TIPO_ITEM[it.tipo] || "Ítem"}>
                            {ICONOS_ITEM[it.tipo] || "📌"}
                          </span>
                          <div className="visita-item-info">
                            <div className="visita-item-nombre">
                              <strong>{it.productoNombre || it.nombreLibre || it.descripcion}</strong>
                              <span className="etiqueta neutra" style={{ fontSize: "0.75rem", marginLeft: "0.5rem" }}>
                                {ETIQUETAS_TIPO_ITEM[it.tipo] || "Ítem"}
                              </span>
                            </div>
                            {(it.dosis || it.via || it.lote) && (
                              <div className="visita-item-detalles muted">
                                {it.dosis && <span>Dosis: {it.dosis}</span>}
                                {it.via && <span> · Vía: {it.via}</span>}
                                {it.lote && <span> · Lote: {it.lote}</span>}
                              </div>
                            )}
                            {it.notas && <p className="visita-item-notas" style={{ margin: "0.25rem 0 0" }}>{it.notas}</p>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Notas generales de la visita */}
                {v.notas && (
                  <div className="visita-notas-pie">
                    <p style={{ margin: 0 }}>
                      <strong>Indicaciones y observaciones:</strong> {v.notas}
                    </p>
                  </div>
                )}

                {/* Fotos y adjuntos de esta visita */}
                {v.adjuntos && v.adjuntos.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-2)", marginBottom: "0.5rem" }}>
                      Fotos y documentos de esta atención ({v.adjuntos.length}):
                    </h3>
                    <div className="mini-galeria-adjuntos">
                      {v.adjuntos.map((adj) => {
                        const esImg = adj.contentType.startsWith("image/");
                        return (
                          <div
                            key={adj.adjuntoID}
                            className="mini-adjunto-tarjeta"
                            onClick={() => setAdjuntoSeleccionado(adj)}
                            title="Clic para ver en grande"
                          >
                            {esImg ? (
                              <img src={adj.thumbnailUrl || adj.url} alt="Foto de la visita" className="mini-adjunto-img" loading="lazy" />
                            ) : (
                              <div className="mini-adjunto-doc">
                                <span>📄</span>
                                <small>PDF</small>
                              </div>
                            )}
                            <div className="mini-adjunto-meta">
                              <span>{formatearBytes(adj.bytes)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <footer className="modal-pie" style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", padding: "0.75rem 1rem", borderTop: "1px solid var(--rule)" }}>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {puedeEditar && v && (
                <>
                  <Link
                    to={`/visitas/${v.visitaID}/editar`}
                    className="boton-chico"
                    onClick={onCerrar}
                  >
                    ✏️ Editar visita
                  </Link>
                  {onEmitirReceta && (
                    <button
                      type="button"
                      className="boton-chico"
                      onClick={() => {
                        onEmitirReceta(v.visitaID, v.diagnostico ?? undefined);
                        onCerrar();
                      }}
                    >
                      📝 Emitir receta
                    </button>
                  )}
                </>
              )}
            </div>

            <button type="button" className="boton-chico" onClick={onCerrar}>
              Cerrar
            </button>
          </footer>
        </div>
      </div>

      {/* Visor lightbox para las fotos del modal */}
      <VisorAdjunto
        adjunto={adjuntoSeleccionado}
        onCerrar={() => setAdjuntoSeleccionado(null)}
        puedeEditar={puedeEditar}
      />
    </>
  );
}
